import { useMemo } from "react";
import { useDebounce } from "@reactuses/core";
import { Trans, useTranslation } from "react-i18next";
import { useServerTable } from "../../../components/table/tableFeatures";
import { MdRotateLeft } from "react-icons/md";
import { css } from "../../../../styled-system/css";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { resetTable, idsToVisibilityState } from "../../../components/table/utils";
import { useBlockWindowQuery } from "../../../components/table/useBlockWindowQuery";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { useStandardEntityMetrics } from "../../merchandising/components/tables/metricUtils";
import { Button } from "../../../components/core/basic/Button";
import { RT8FilterWidget } from "../../merchandising/components/tables/FilterWidget";
import { useGetDSPAudiencePaginated } from "../hooks/useDSPAudiencePaginatedQuery";
import type { DSPAudienceRow } from "../../../zodios/apiDefinitions/api/dsp/audienceDatasets";
import AudienceExpander from "./AudienceExpander";
import { trellisPalette } from "../../../components/custom/analytics/palettes";

const columnHelper = createColumnBuilder<DSPAudienceRow>();

const defaultSelectedColumns = [
  "dsp_ecpm",
  "dsp_impressions",
  "dsp_clicks",
  "dsp_cost",
  "dsp_total_purchases",
  "dsp_units",
  "dsp_ctr",
];

type Props = {
  adGroupId: string | number;
  untargetedReport?: boolean;
  totalImpressions?: number;
};

const DSPAudiencesTable = ({ adGroupId, untargetedReport = false, totalImpressions = 0 }: Props) => {
  const { t } = useTranslation(["dsp", "common"]);
  const standardMetricIds = useStandardEntityMetrics("dspAudience");

  const defaultColumnVisibility = useMemo(
    () =>
      idsToVisibilityState(
        [
          "segment_name",
          "targeting_method",
          ...(untargetedReport ? ["overlap_impression_share"] : []),
          ...defaultSelectedColumns,
        ],
        ["segment_name", "targeting_method", "line_item_id", "overlap_impression_share", ...standardMetricIds],
      ),
    [standardMetricIds, untargetedReport],
  );

  const persistKey = untargetedReport ? "dspAudiencesOverlap" : "dspAudiences";

  const { state, initialState, setters } = usePersistedTableState({
    persistKey,
    initialState: {
      columnVisibility: defaultColumnVisibility,
      sorting: [{ id: "dsp_impressions", desc: true }],
    },
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor("segment_name", {
        id: "segment_name",
        header: t(($) => $.dsp["Segment"]),
        minSize: 200,
        size: 300,
        maxSize: 500,
        enableColumnFilter: false,
        cell: ({ getValue }) => <span className={css({ fontWeight: "medium" })}>{getValue()}</span>,
        meta: {
          columnSelectorOptions: { label: t(($) => $.dsp["Segment"]) },
        },
      }),
      columnHelper.accessor("targeting_method", {
        id: "targeting_method",
        header: t(($) => $.dsp["Targeting Method"]),
        size: 170,
        meta: {
          columnFilterOptions: { format: "select" },
          columnSelectorOptions: { label: t(($) => $.dsp["Targeting Method"]) },
        },
      }),
      columnHelper.display({
        id: "line_item_id",
        header: t(($) => $.dsp["Line Item ID"]),
        size: 140,
        cell: ({ row }) => {
          const original = row.original as Record<string, unknown>;
          const lineItemId = original.line_item_id;
          return typeof lineItemId === "string" || typeof lineItemId === "number" ? String(lineItemId) : "";
        },
      }),
      columnHelper.display({
        id: "overlap_impression_share",
        header: t(($) => $.dsp["Share of Total Impressions"]),
        size: 185,
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const audienceImpressions = row.original.impressions || 0;
          const sharePct = totalImpressions > 0 ? (audienceImpressions / totalImpressions) * 100 : 0;

          return (
            <div
              className={css({
                position: "relative",
                width: "100px",
                height: "12px",
                borderRadius: "3px",
                backgroundColor: "#EEE",
              })}
            >
              <div
                className={css({
                  position: "absolute",
                  top: 0,
                  left: 0,
                  height: "12px",
                  borderRadius: "3px",
                })}
                style={{
                  width: `${sharePct}%`,
                  backgroundColor: trellisPalette[2],
                }}
              />
            </div>
          );
        },
      }),
      ...standardMetricIds.map((id) => columnHelper.metric(id)),
    ],
    [standardMetricIds, totalImpressions, t],
  );

  const debouncedSearch = useDebounce(state.globalFilter, 500);

  const getQueryOptions = useGetDSPAudiencePaginated({
    lineItemId: adGroupId,
    untargetedReport,
  });
  const audienceQuery = useBlockWindowQuery(getQueryOptions, {
    pagination: state.pagination,
    sorting: state.sorting,
    filters: state.columnFilters,
    search: debouncedSearch,
  });

  const tableData = useMemo<DSPAudienceRow[]>(() => audienceQuery.data?.data ?? [], [audienceQuery.data]);
  const showEmptyState = !untargetedReport && !audienceQuery.isFetching && (audienceQuery.data?.count ?? 0) === 0;

  const table = useServerTable({
    data: tableData,
    columns,
    initialState,
    state,
    ...setters,
    rowCount: audienceQuery.data?.count ?? undefined,
    getRowId: (row) => String(row.segment_id ?? ""),
    getRowCanExpand: () => true,
    meta: {
      showSkeleton: audienceQuery.isFetching && (!audienceQuery.data || audienceQuery.isPlaceholderData),
    },
  });

  return (
    <>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions.Search)}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        className={css({ minWidth: "100%" })}
        renderExpander={(row) => <AudienceExpander row={row} lineItemId={adGroupId} />}
      />
      {showEmptyState && (
        <div
          className={css({
            paddingY: "xlarge",
            marginX: "auto",
            textAlign: "center",
            color: "text.secondary",
            fontSize: "medium",
            maxWidth: "800px",
          })}
        >
          <Trans
            t={t}
            i18nKey={($) =>
              $.dsp[
                "No audience targets to display. If this line item uses Performance+, audience selection is automated — see the <audienceOverlap>Audience Overlap</audienceOverlap> tab for the segments your impressions reached."
              ]
            }
            components={{ audienceOverlap: <strong /> }}
          />
        </div>
      )}
    </>
  );
};

export default DSPAudiencesTable;
